import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Cache TTL configuration (in milliseconds)
export const CACHE_TTL = {
  DASHBOARD: 2 * 60 * 1000,      // 2 minutes
  CAMPAIGNS: 5 * 60 * 1000,      // 5 minutes
  LEADS: 5 * 60 * 1000,          // 5 minutes
  AUTOMATION: 2 * 60 * 1000,     // 2 minutes
  INBOX: 1 * 60 * 1000,          // 1 minute
  STATIC: 15 * 60 * 1000,        // 15 minutes (templates, settings)
};

// Query key constants for cache management
export const QUERY_KEYS = {
  DASHBOARD: ['dashboard'],
  DASHBOARD_STATS: ['dashboard', 'stats'],
  AUTOMATION_STATS: ['dashboard', 'automation'],
  AUTOMATION_TIMELINE: ['dashboard', 'timeline'],
  CAMPAIGNS: ['campaigns'],
  CAMPAIGN_STATS: ['campaigns', 'stats'],
  LEADS: ['leads'],
  LEAD_EVENTS: ['leadEvents'],
  AUTOMATION_SEQUENCES: ['automation', 'sequences'],
  AUTOMATION_OPENS: ['automation', 'opens'],
  AUTOMATION_PENDING: ['automation', 'pending'],
  AUTOMATION_ANALYTICS: ['automation', 'analytics'],
  TEMPLATES: ['automation', 'templates'],
  SETTINGS: ['automation', 'settings'],
  INBOX: ['inbox'],
  MESSAGES: ['messages'],
  CONVERSATIONS: ['conversations'],
  MEETINGS: ['meetings'],
  NOTES: ['notes'],
  TASKS: ['tasks'],
};

// Create QueryClient with optimal defaults
// Memory-only cache (no persistence) - cleared on logout
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Don't refetch on window focus by default (reduces unnecessary DB calls)
      refetchOnWindowFocus: false,
      // Don't refetch on reconnect automatically
      refetchOnReconnect: 'always',
      // Default stale time - data is considered fresh for 2 minutes
      staleTime: CACHE_TTL.DASHBOARD,
      // Cache time - keep unused data in memory for 10 minutes before GC
      gcTime: 10 * 60 * 1000,
      // Don't retry failed queries aggressively
      retry: 1,
      retryDelay: 1000,
      // Don't refetch on mount if data is fresh
      refetchOnMount: false,
    },
  },
});

// Export queryClient for direct access (cache invalidation from real-time subs)
export { queryClient };

// Clear all cached data (call on logout)
export const clearAllCache = () => {
  queryClient.clear();
};

// Invalidate specific cache keys (call after write operations)
export const invalidateCache = (keys) => {
  if (Array.isArray(keys)) {
    keys.forEach(key => queryClient.invalidateQueries({ queryKey: key }));
  } else {
    queryClient.invalidateQueries({ queryKey: keys });
  }
};

// Update specific cache entry with new data (for real-time subscription updates)
export const updateCacheEntry = (key, updater) => {
  queryClient.setQueryData(key, updater);
};

export function QueryProvider({ children }) {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
    </QueryClientProvider>
  );
}

export default QueryProvider;
